/**
 * 清新绿意模板 - 首页
 * 
 * 设计风格（有机自然杂志风，与 Classic / Modern 完全不同）：
 * - 全宽 Hero，有机曲线背景 + 流动渐变装饰
 * - 分类采用浮动气泡卡片式展示
 * - 信息列表采用杂志式三列图文卡片（交错视觉）
 * - 绿意 + 自然色调
 */

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { getSiteInfo, getSiteCategories, getListings, type ListingItem, type FrontendCategory } from "@/utils/api";
import { useSite } from "@/context/SiteContext";

export default function GreenHomePage() {
  const navigate = useNavigate();
  const { siteId, loading: siteLoading } = useSite();

  const [searchConfig, setSearchConfig] = useState({ enabled: true, disabledMessage: "" });
  const [categories, setCategories] = useState<FrontendCategory[]>([]);
  const [listings, setListings] = useState<ListingItem[]>([]);
  const [loadingListings, setLoadingListings] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchTip, setShowSearchTip] = useState(false);

  useEffect(() => {
    getSiteInfo().then((info) => {
      if (info) {
        document.title = info.seo_title || '';
        if (info.search_enabled !== undefined) {
          setSearchConfig({
            enabled: info.search_enabled !== false,
            disabledMessage: info.search_disabled_message || "搜索功能已关闭",
          });
        }
      }
    });
  }, []);

  useEffect(() => {
    if (siteLoading || !siteId) return;
    getSiteCategories()
      .then((cats) => setCategories(cats.slice(0, 8)))
      .catch(console.error)
      .finally(() => setLoadingCategories(false));
  }, [siteId, siteLoading]);

  useEffect(() => {
    if (siteLoading || !siteId) return;
    getListings({ page_size: 9 })
      .then((data) => setListings(data.list || []))
      .catch(console.error)
      .finally(() => setLoadingListings(false));
  }, [siteId, siteLoading]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchConfig.enabled) {
      setShowSearchTip(true);
      setTimeout(() => setShowSearchTip(false), 3000);
      return;
    }
    if (searchQuery.trim()) navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      {/* ── Hero 有机自然风 ── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-500 via-green-600 to-teal-700 min-h-[520px] flex items-center">
        {/* 有机装饰曲线 */}
        <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 1200 600" preserveAspectRatio="none">
          <defs>
            <radialGradient id="glow1" cx="20%" cy="30%"><stop offset="0%" stopColor="#a7f3d0"/><stop offset="100%" stopColor="transparent"/></radialGradient>
            <radialGradient id="glow2" cx="80%" cy="70%"><stop offset="0%" stopColor="#6ee7b7"/><stop offset="100%" stopColor="transparent"/></radialGradient>
          </defs>
          <rect width="1200" height="600" fill="url(#glow1)" />
          <rect width="1200" height="600" fill="url(#glow2)" />
          <ellipse cx="100" cy="480" rx="350" ry="180" fill="white" opacity="0.06" />
          <ellipse cx="1050" cy="120" rx="280" ry="160" fill="white" opacity="0.05" />
        </svg>

        {/* 漂浮叶子装饰 */}
        <div className="absolute top-12 right-16 text-white/10 text-8xl select-none rotate-12">🌿</div>
        <div className="absolute bottom-20 left-12 text-white/10 text-7xl select-none -rotate-12">🍃</div>

        <div className="relative w-full max-w-6xl mx-auto px-4 sm:px-6 py-16 md:py-20 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/15 backdrop-blur-sm rounded-full text-white/90 text-sm mb-6">
            <span className="w-1.5 h-1.5 bg-green-300 rounded-full animate-pulse" />
            发现优质生活服务
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-4">
            让每一次选择
            <br />
            <span className="bg-gradient-to-r from-lime-200 via-emerald-200 to-teal-200 bg-clip-text text-transparent">
              都充满生机
            </span>
          </h1>
          <p className="text-base md:text-lg text-emerald-100/80 max-w-xl mx-auto mb-10 leading-relaxed">
            汇聚全城优质服务，为您的生活增添一抹清新绿意
          </p>

          {/* 搜索框 - 有机圆角 */}
          <form onSubmit={handleSearch} className="max-w-xl mx-auto">
            <div className="relative flex items-center bg-white rounded-3xl shadow-2xl shadow-emerald-900/20 overflow-hidden border border-white/20">
              <i className="ri-search-line pl-5 text-emerald-400 text-xl" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索您需要的服务..."
                className="flex-1 py-4 px-3 text-sm md:text-base text-gray-700 placeholder-gray-400 focus:outline-none"
              />
              <button
                type="submit"
                className="m-1.5 px-7 py-2.5 bg-gradient-to-r from-emerald-500 to-green-600 text-white font-semibold text-sm rounded-3xl hover:from-emerald-400 hover:to-green-500 hover:shadow-lg transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                探索
              </button>
            </div>
            {showSearchTip && (
              <div className="mt-3 px-4 py-2 bg-white/20 backdrop-blur-sm text-white text-sm rounded-2xl inline-block">
                <i className="ri-error-warning-line mr-1" />
                {searchConfig.disabledMessage || "搜索功能已关闭"}
              </div>
            )}
          </form>
        </div>

        {/* 底部波浪 */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" preserveAspectRatio="none">
            <path d="M0 80L60 66.7C120 53.3 240 26.7 360 20C480 13.3 600 26.7 720 33.3C840 40 960 40 1080 33.3C1200 26.7 1320 13.3 1380 6.7L1440 0V80H1380C1320 80 1200 80 1080 80C960 80 840 80 720 80C600 80 480 80 360 80C240 80 120 80 60 80H0Z" fill="#faf7f5"/>
          </svg>
        </div>
      </section>

      {/* ── 服务分类 ── 浮动气泡风格 ── */}
      <section className="py-14 md:py-18">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 bg-emerald-50 text-emerald-600 text-xs font-semibold rounded-full uppercase tracking-wider mb-3">服务分类</span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800">探索服务领域</h2>
            <p className="text-sm text-gray-500 mt-2">找到您感兴趣的服务类型，开启探索之旅</p>
          </div>

          {loadingCategories ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {[...Array(8)].map(i => (
                <div key={i} className="animate-pulse bg-white rounded-3xl p-5 flex flex-col items-center gap-3 border border-gray-100">
                  <div className="w-14 h-14 bg-gray-200 rounded-2xl" />
                  <div className="w-16 h-3.5 bg-gray-200 rounded" />
                  <div className="w-12 h-2.5 bg-gray-100 rounded" />
                </div>
              ))}
            </div>
          ) : categories.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {categories.map((cat, index) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group relative bg-white rounded-3xl p-5 flex flex-col items-center text-center border border-gray-100 hover:border-emerald-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-500 overflow-hidden"
                  style={{ transitionDelay: `${index * 50}ms` }}
                >
                  {/* 装饰背景 */}
                  <div className="absolute -top-6 -right-6 w-16 h-16 bg-gradient-to-br from-emerald-50 to-green-50 rounded-full opacity-0 group-hover:opacity-100 group-hover:scale-150 transition-all duration-500" />

                  <div className="relative w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-50 to-green-100 text-emerald-600 group-hover:from-emerald-500 group-hover:to-green-600 group-hover:text-white transition-all duration-500 text-xl md:text-2xl shadow-sm group-hover:shadow-emerald-200">
                    {cat.icon ? <i className={`${cat.icon}`} /> : <i className="ri-leaf-line" />}
                  </div>
                  <span className="relative mt-3 font-semibold text-gray-700 text-sm md:text-base line-clamp-1 group-hover:text-emerald-700 transition-colors">{cat.name}</span>
                  <span className="relative text-xs text-gray-400 mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">查看详情 →</span>
                </Link>
              ))}
            </div>
          ) : null}
        </div>
      </section>

      {/* ── 最新发布 ── 杂志式三列图文 ── */}
      <section className="py-14 md:py-18 bg-gradient-to-b from-white to-emerald-50/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-end justify-between mb-8">
            <div>
              <span className="inline-block px-3 py-1 bg-emerald-50 text-emerald-600 text-xs font-semibold rounded-full uppercase tracking-wider mb-2">最新发布</span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800">发现新上线的服务</h2>
            </div>
            <Link to="/category/all" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600 hover:text-emerald-700 transition-colors">
              浏览全部 <i className="ri-arrow-right-line" />
            </Link>
          </div>

          {loadingListings ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map(i => (
                <div key={i} className="animate-pulse bg-white rounded-3xl overflow-hidden border border-gray-100">
                  <div className="w-full h-48 bg-gray-200" />
                  <div className="p-5 space-y-3">
                    <div className="w-3/4 h-5 bg-gray-200 rounded-lg" />
                    <div className="w-full h-3 bg-gray-100 rounded" />
                    <div className="w-2/3 h-3 bg-gray-100 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : listings.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.map((listing, index) => (
                <Link
                  key={listing.id}
                  to={`/listing/${listing.id}`}
                  className={`group bg-white rounded-3xl overflow-hidden border border-gray-100 hover:border-emerald-200 hover:shadow-xl transition-all duration-500 hover:-translate-y-1`}
                >
                  {/* 图片区 */}
                  <div className={`relative overflow-hidden bg-gradient-to-br from-emerald-100 to-green-50 aspect-square`}>
                    {listing.images?.[0] ? (
                      <img src={listing.images[0]} alt={listing.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-image-line text-3xl text-emerald-300" />
                      </div>
                    )}
                    {listing.is_pinned && (
                      <span className="absolute top-3 left-3 px-2.5 py-1 bg-emerald-500 text-white text-xs font-bold rounded-xl shadow-sm">
                        <i className="ri-pushpin-line mr-1" />置顶
                      </span>
                    )}
                    <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>

                  {/* 内容区 */}
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="px-2.5 py-0.5 bg-emerald-50 text-emerald-600 text-xs font-medium rounded-full">
                        {listing.category?.name || '未分类'}
                      </span>
                      <span className="text-xs text-gray-400">{formatTimeAgo(listing.created_at)}</span>
                    </div>
                    <h3 className="text-base font-bold text-gray-800 line-clamp-2 group-hover:text-emerald-600 transition-colors">
                      {listing.title}
                    </h3>
                    <p className="text-xs text-gray-500 leading-relaxed mt-2 line-clamp-2">
                      {listing.content}
                    </p>
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-50">
                      <div className="flex items-center gap-1.5 text-xs text-gray-400">
                        <i className="ri-user-line" />
                        <span>{listing.contact_name || '匿名'}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        <span className="flex items-center gap-1"><i className="ri-eye-line" />{listing.view_count}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-gray-200">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-emerald-50">
                <i className="ri-inbox-line text-2xl text-emerald-400" />
              </div>
              <p className="text-gray-500 font-medium">暂无信息</p>
            </div>
          )}

          <div className="mt-8 text-center sm:hidden">
            <Link to="/category/all" className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-600 hover:text-emerald-700">
              浏览全部发布 <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-green-600 to-teal-700" />
        <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 800 400">
          <circle cx="150" cy="100" r="120" fill="white" />
          <circle cx="650" cy="300" r="180" fill="white" />
          <circle cx="400" cy="200" r="80" fill="white" fillOpacity="0.5" />
        </svg>
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">准备好发布您的服务了吗？</h2>
          <p className="text-emerald-100 mb-8">加入我们，让更多人发现您的优质服务</p>
          <button
            onClick={() => navigate("/publish")}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-emerald-600 font-bold rounded-3xl hover:bg-emerald-50 hover:shadow-xl transition-all duration-300 cursor-pointer hover:-translate-y-0.5"
          >
            <i className="ri-add-circle-line" />
            立即发布
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "昨天";
  if (days < 7) return `${days}天前`;
  return `${date.getMonth() + 1}月${date.getDate()}日`;
}
